import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchBelief } from "@/lib/api";
import { Tag } from "@/components/Tag";
import { fmtDate } from "@/lib/format";
import { BeliefEditor } from "@/components/BeliefEditor";
import { StoryGenerator } from "@/components/StoryGenerator";
import { ArrowLeft, Pencil } from "lucide-react";

export default function BeliefDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchBelief(id);
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return (
      <div data-testid="belief-detail-loading" className="text-sm text-[#141B4D]/60">
        Loading…
      </div>
    );
  if (!data?.belief)
    return (
      <div data-testid="belief-detail-notfound" className="text-sm text-[#141B4D]/60">
        Belief not found.{" "}
        <Link to="/beliefs" className="underline">
          Back to list
        </Link>
      </div>
    );

  const b = data.belief;
  const allStories = data.stories || [];
  const pendingStories = allStories.filter((s) => s.review_status === "pending_review");
  const stories = allStories.filter((s) => s.review_status !== "pending_review");

  return (
    <div data-testid="belief-detail-page">
      <Link
        to="/beliefs"
        data-testid="back-to-beliefs"
        className="inline-flex items-center gap-2 text-xs text-[#141B4D]/60 hover:text-[#141B4D] mb-6"
      >
        <ArrowLeft size={12} /> Back to beliefs
      </Link>

      <div className="bg-white border border-[#141B4D]/15 rounded-sm p-8 md:p-12 mb-10">
        <div className="flex items-start justify-between gap-6 mb-6">
          <div className="text-xs uppercase tracking-[0.15em] text-[#141B4D]/60">
            Belief detail
          </div>
          <button
            onClick={() => setEditing(true)}
            data-testid="belief-detail-edit-button"
            className="inline-flex items-center gap-2 bg-transparent text-[#141B4D] border border-[#141B4D]/20 px-3 py-1.5 rounded-sm text-xs hover:bg-[#141B4D]/5"
          >
            <Pencil size={12} /> Edit
          </button>
        </div>

        <h1
          data-testid="belief-detail-text"
          className="text-3xl md:text-4xl font-bold text-[#141B4D] leading-snug tracking-tight"
        >
          {b.belief_text}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 pt-8 border-t border-[#141B4D]/15">
          <Meta label="Category" value={b.category || "—"} />
          <Meta label="Status">
            <Tag variant={b.status === "Active" ? "active" : "retired"}>
              {b.status}
            </Tag>
          </Meta>
          <Meta label="Date Added" value={fmtDate(b.date_added)} />
          <Meta label="Source" value={b.source || "—"} />
          <Meta label="Offer Tags">
            <div className="flex flex-wrap gap-1.5">
              {(b.offer_tags || []).length === 0 ? (
                <span className="text-sm text-[#141B4D]/50">—</span>
              ) : (
                (b.offer_tags || []).map((t) => <Tag key={t}>{t}</Tag>)
              )}
            </div>
          </Meta>
          <Meta label="Linked Stories" value={String(stories.length)} />
        </div>

        {b.notes && (
          <div className="mt-8 pt-6 border-t border-[#141B4D]/15">
            <div className="text-xs uppercase tracking-[0.05em] text-[#141B4D]/60 mb-2">
              Notes
            </div>
            <p className="text-sm text-[#141B4D]/85 leading-relaxed whitespace-pre-wrap">
              {b.notes}
            </p>
          </div>
        )}
      </div>

      <section data-testid="belief-detail-stories">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#141B4D] tracking-tight">
            Linked stories
          </h2>
          <span className="text-xs text-[#141B4D]/60">
            {stories.length} approved
            {pendingStories.length > 0 && ` · ${pendingStories.length} pending`}
          </span>
        </div>

        <div className="mb-8">
          <StoryGenerator
            belief={b}
            pendingStories={pendingStories}
            onChange={load}
          />
        </div>

        {stories.length === 0 ? (
          <div className="border border-dashed border-[#141B4D]/25 rounded-sm p-8 text-center text-sm text-[#141B4D]/60">
            No stories linked yet. This belief is a{" "}
            <Link
              to="/gaps"
              className="underline decoration-[#FFD100] decoration-2 underline-offset-2"
            >
              gap
            </Link>
            . Create a story and link it to fill the gap.
          </div>
        ) : (
          <div className="space-y-4">
            {stories.map((s) => (
              <article
                key={s.id}
                data-testid={`linked-story-${s.id}`}
                className="bg-white border border-[#141B4D]/15 rounded-sm p-6"
              >
                <div className="flex items-center gap-3 mb-3">
                  {s.story_type && <Tag>{s.story_type}</Tag>}
                  <Tag
                    variant={
                      s.freshness === "Evergreen"
                        ? "evergreen"
                        : s.freshness === "Time-sensitive"
                          ? "time-sensitive"
                          : "stale"
                    }
                  >
                    {s.freshness}
                  </Tag>
                  <span className="text-xs text-[#141B4D]/50 ml-auto">
                    {fmtDate(s.date_added)}
                  </span>
                </div>
                <p className="text-sm text-[#141B4D] leading-relaxed whitespace-pre-wrap">
                  {s.story_text}
                </p>
                {s.source && (
                  <div className="text-xs text-[#141B4D]/50 mt-3">
                    Source: {s.source}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      <BeliefEditor
        open={editing}
        belief={b}
        onClose={() => setEditing(false)}
        onSaved={() => load()}
      />
    </div>
  );
}

function Meta({ label, value, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.1em] text-[#141B4D]/60 mb-2">
        {label}
      </div>
      {children || (
        <div className="text-sm text-[#141B4D]">{value}</div>
      )}
    </div>
  );
}
